/**
 * Telemetry URL policy (REF-6708 privacy slice B): what Sentry (and later GA) may see of a URL, a
 * route or a path. Rules are in `telemetryUrl.core.js`; this module supplies the app's data:
 *
 * - app routes are DERIVED from `PATHS` — any pattern with a `:token`, `:orderId` or `:stateId`
 *   segment is covered automatically, so a new `/foo/:token` route needs no edit here;
 * - API token paths are a short hand-written list, pinned by a tripwire test over the services;
 * - query values survive only under a listed key AND when they pass that key's validator.
 *
 * TELEMETRY COPIES ONLY — never pass a URL used for a request, navigation, download or signature.
 */
import { PATHS } from "router/config/paths";
import { TECHPACK_CREATE_FLOW_ID } from "pages/TechPackPage/utils/techpackCreateFlow";
import { EDIT_TYPE_TO_IMAGE_TYPE } from "pages/StudioProjectDetailPage/studioEditTypes";
import { createTelemetryUrl } from "./telemetryUrl.core";

/** Route params whose VALUE is a credential or a sensitive id. */
export const SECRET_ROUTE_PARAMS = ["token", "orderId", "stateId"] as const;

/** API path prefixes whose NEXT segment is a credential (`users/reset_password/<token>`, …). */
export const API_SECRET_PREFIXES = ["users/reset_password", "users/verify_email", "teams/accept_invite", "teams/accept_member_invite"] as const;

type RuleSegment = { literal?: string; secret?: boolean };

const routeRule = (pattern: string): RuleSegment[] | null => {
  const segments = pattern.split("/").filter(Boolean);
  const rule = segments.map((segment): RuleSegment => {
    if (!segment.startsWith(":")) return { literal: segment.toLowerCase() };
    const name = segment.slice(1).replace(/\?$/, "");

    return (SECRET_ROUTE_PARAMS as readonly string[]).includes(name) ? { secret: true } : {};
  });

  return rule.some((segment) => segment.secret) ? rule : null;
};

/** The app-route rules, one per `PATHS` pattern that carries a secret param. Exported for the tripwire. */
export const APP_SECRET_RULES = Object.values(PATHS)
  .map((pattern) => routeRule(pattern))
  .filter((rule): rule is RuleSegment[] => rule !== null);

/**
 * REF-6858 (P1-a′, the user's option 1): secret route params that are PURCHASE identifiers, not access secrets — a
 * Stripe-flow id that no endpoint accepts (handoffs/backend-purchase-ids.md). A secret route whose secret params are
 * ALL of these keeps the Google tag, so the GA / Google Ads purchase conversion stays as it is; New Relic is
 * scrubbed on every secret route regardless. Google Ads may still observe that identifier in the page URL.
 */
const PURCHASE_ID_PARAMS = ["orderId", "stateId"] as const;

const isPurchaseRoute = (pattern: string): boolean => {
  const secrets = pattern
    .split("/")
    .filter((segment) => segment.startsWith(":"))
    .map((segment) => segment.slice(1).replace(/\?$/, ""))
    .filter((name) => (SECRET_ROUTE_PARAMS as readonly string[]).includes(name));

  return secrets.length > 0 && secrets.every((name) => (PURCHASE_ID_PARAMS as readonly string[]).includes(name));
};

/** The secret app routes that keep the Google tag (derived from `PATHS`, like `APP_SECRET_RULES`). */
export const GOOGLE_TAG_EXEMPT_RULES = Object.values(PATHS)
  .filter(isPurchaseRoute)
  .map((pattern) => routeRule(pattern))
  .filter((rule): rule is RuleSegment[] => rule !== null);

/**
 * D2 (user decision, 2026-10-31): the link pages main's head bootstrap rewrites to a TOKENLESS address — every secret
 * route whose secret param is OPTIONAL (`/reset-password/:token?`, …). The tokenless address still carries `?email=`,
 * so a policy change to its QUERY or fragment there counts as a token landing too (New Relic replay, the Google tag,
 * the Meta PageView). The routes' literal segments, derived from `PATHS` like `APP_SECRET_RULES` — never hand-listed.
 */
export const TOKENLESS_LINK_RULES: string[][] = Object.values(PATHS)
  .filter((pattern) => pattern.split("/").some((segment) => segment.startsWith(":") && segment.endsWith("?") && (SECRET_ROUTE_PARAMS as readonly string[]).includes(segment.slice(1, -1))))
  .map((pattern) =>
    pattern
      .split("/")
      .filter((segment) => segment !== "" && !segment.startsWith(":"))
      .map((segment) => segment.toLowerCase())
  );

/** True when `raw` is a tokenless link page (`TOKENLESS_LINK_RULES`) whose query or fragment the policy changed. */
export const isTokenlessLinkLanding = (raw: URL, clean: URL): boolean => {
  const parts = raw.pathname
    .split("/")
    .filter((part) => part !== "")
    .map((part) => part.toLowerCase());
  const linkPage = TOKENLESS_LINK_RULES.some((rule) => rule.length === parts.length && rule.every((literal, i) => parts[i] === literal));

  return linkPage && (clean.search !== raw.search || clean.hash !== raw.hash);
};

/** A query-value check, as DATA so the head bootstrap can carry it (evaluated only by the core). */
export type QueryValidator = { kind: "id" } | { kind: "enum"; values: string[] } | { kind: "attribution" };

const ID: QueryValidator = { kind: "id" };
// An explicit retention exception for ad attribution, NOT a credential detector (see the core).
const ATTRIBUTION: QueryValidator = { kind: "attribution" };
const oneOf = (...values: string[]): QueryValidator => ({ kind: "enum", values });

/**
 * Query keys whose value may be kept, each with the check its value must pass. Enum sets come from
 * the readers: `tab` StudioProjectsPage + notificationDeepLink, `view` notificationDeepLink +
 * SvgEditorProvider, `src` moodboardDetailRoute (MoodboardSource), `create` KeysScreen, `guide` the
 * guided-flow registry, `focusEditType` the studio edit-type map its reader looks it up in. `type` has no
 * reader, so it is not listed (its value is redacted). `_gl` (the GA linker) is listed but its value
 * contains `*`, so the attribution check redacts it in TELEMETRY copies — GA's linker reads it from the
 * live landing URL, which is never rewritten (REF-6708 D2).
 */
export const QUERY_VALIDATORS: Record<string, QueryValidator> = {
  id: ID,
  templateId: ID,
  projectId: ID,
  techpackProjectId: ID,
  focusComment: ID,
  focusElement: ID,
  focusResult: ID,
  focusProduct: ID,
  focusImage: ID,
  tab: oneOf("my", "shared", "document", "views"),
  view: oneOf("front", "back", "side"),
  src: oneOf("my", "curated", "catalogue"),
  create: oneOf("1"),
  guide: oneOf(TECHPACK_CREATE_FLOW_ID),
  focusEditType: oneOf(...Object.keys(EDIT_TYPE_TO_IMAGE_TYPE)),
  utm_source: ATTRIBUTION,
  utm_medium: ATTRIBUTION,
  utm_campaign: ATTRIBUTION,
  utm_term: ATTRIBUTION,
  utm_content: ATTRIBUTION,
  gclid: ATTRIBUTION,
  gad_source: ATTRIBUTION,
  gbraid: ATTRIBUTION,
  wbraid: ATTRIBUTION,
  fbclid: ATTRIBUTION,
  _gl: ATTRIBUTION,
};

/** Keys whose string value is a URL / route (sanitised as one, not as free text) in a deep walk. */
const URL_KEYS = ["url", "http.url", "url.full", "from", "to", "route", "from_route", "to_route", "page_route", "request_path", "result_url", "image_url", "href"] as const;

/**
 * The whole policy as plain JSON-serialisable data — what the core is built from here AND what
 * `scripts/gen-telemetry-bootstrap.mjs` serialises into the inline head script of `public/index.html`.
 */
export const TELEMETRY_RULES = {
  appRules: APP_SECRET_RULES,
  apiPrefixes: API_SECRET_PREFIXES.map((prefix) => prefix.split("/")),
  queryValidators: QUERY_VALIDATORS,
  urlKeys: [...URL_KEYS],
};

const policy = createTelemetryUrl(TELEMETRY_RULES);

export const REDACTED: string = policy.REDACTED;

/** Full or relative URL in, same form out (origin kept if present). Never throws; idempotent; pure. */
export const sanitizeUrl: (input: string) => string = policy.sanitizeUrl;

/** Path (+ query) only — for tags.route, fingerprints and transaction names. */
export const sanitizeRoute: (input: string) => string = policy.sanitizeRoute;

/**
 * `sanitizeRoute` for values that are USUALLY a label (fingerprint entries, span / transaction names, the
 * DSC transaction): a value with no `/`, `?`, `#` or `@` cannot carry a route, so it is kept as written
 * (`media-load-failure`, `label:phase`, `{{ default }}`); anything route-like goes through `sanitizeRoute`.
 */
export const sanitizeRouteOrLabel = (value: string): string => (/[/?#@]/.test(value) ? sanitizeRoute(value) : value);

/** A bare query string (without `?`) under the P3 policy. */
export const sanitizeQuery: (query: string) => string = policy.sanitizeQuery;

/** Free text that may EMBED urls / paths. Not a general secret detector. */
export const sanitizeUrlsInText: (text: string) => string = policy.sanitizeUrlsInText;

/** Copy of a JSON-ish value with every string sanitised (depth ≤ 6, cycles and class instances fail closed). */
export const sanitizeDeep: <T>(value: T) => T = policy.sanitizeDeep;
